// Angular imports
import { Component, OnInit, ViewChild } from '@angular/core';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// Rxjs imports
import { Subscription, forkJoin } from 'rxjs';

// Component imports
import { CommunityOwnerAlertsListComponent } from './community-owner-alerts-list/community-owner-alerts-list.component';

// Service imports
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ContextManagerService } from '../../_services/data-services/context-manager.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { AlertsService } from '../_services/alerts.service';
import { CiraHelpService } from '../../_services/shared-services/cira-help.service';
import { UserService } from '../../_services/data-services/user.service';

// Model imports
import { PropertyCommunityInfoResultDto } from '../../_models/property-community-info-result-dto';
import { CommunityAlertResultDto } from '../_models/community-alert-result-dto';
import { OwnerAlertResultDto } from '../_models/owner-alert-result-dto';
import { AlertResultDto } from '../_models/alert-result-dto';

// Constant imports
import { CiraHelpKeyConstants } from '../../_constants/cira-help-key-constants';

@Component({
    selector: 'app-community-owner-alerts',
    templateUrl: './community-owner-alerts.component.html',
    styleUrls: ['./community-owner-alerts.component.scss']
})
export class CommunityOwnerAlertsComponent implements OnInit {
    @ViewChild('alertsList', { static: false }) alertsList: CommunityOwnerAlertsListComponent;

    visible: boolean;
    loadingVisible: boolean;
    isSmallScreen: boolean;
    popupWidth = '40%';
    popupHeight = '70%';

    dateFormat: string;
    viewModes: any[];
    tabAlerts: any[];
    selectedTabData: CommunityAlertResultDto | OwnerAlertResultDto;
    selectedViewIndex = -1;
    isOwnerContext = false;
    helpLink: string;

    selectedContext: PropertyCommunityInfoResultDto;
    contextChangeSubscription: Subscription;

    iconClose = IconConstants.CLOSE;

    constructor(
        private readonly screenService: ScreenService,
        private readonly errorService: ServerErrorService,
        private readonly contextService: ContextManagerService,
        private readonly alertServices: AlertsService,
        private readonly ciraHelpService: CiraHelpService,
        private readonly userService: UserService
    ) {}

    ngOnInit(): void {
        this.dateFormat = 'M/d/yyyy';
        this.updateScreenSize();
        this.screenService.changed.subscribe(this.updateScreenSize.bind(this));

        this.selectedContext = this.contextService.getSelectedContext();
        this.helpLink = this.ciraHelpService.getHelpLinkByKey(
            CiraHelpKeyConstants.portalAlerts,
            !!this.selectedContext ? this.selectedContext.communityId : null
        );
        this.contextChangeSubscription = this.contextService.getContext().subscribe(() => {
            this.selectedContext = this.contextService.getSelectedContext();
            this.helpLink = this.ciraHelpService.getHelpLinkByKey(
                CiraHelpKeyConstants.portalAlerts,
                !!this.selectedContext ? this.selectedContext.communityId : null
            );
            if (!!this.selectedContext && !this.visible) {
                setTimeout(this.show.bind(this), 0);
            }
        });
    }

    show(forceShow = false, clearCache = true): void {
        this.viewModes = [];
        this.tabAlerts = [];
        this.selectedViewIndex = -1;

        const isInternal = this.userService.isInternal();
        const observables = [];
        this.isOwnerContext = Utils.notNullAndDefined(this.selectedContext.customerId);
        if (Utils.isTrue(this.isOwnerContext) && Utils.isTrue(isInternal)) {
            observables.push(
                this.alertServices.getOwnerAlerts(
                    this.selectedContext.customerId,
                    this.selectedContext.communityId,
                    clearCache
                )
            );
            this.viewModes.push({
                id: 'owner-alerts',
                text: `Owner Alerts`
            });
        }

        if (Utils.isTrue(isInternal)) {
            observables.push(this.alertServices.getCommunityAlerts(this.selectedContext.communityId, clearCache));
            this.viewModes.push({
                id: 'community-alerts',
                text: `Community Alerts`
            });
        }

        if (Utils.notNullOrEmpty(observables)) {
            this.loadingVisible = true;
            forkJoin(observables).subscribe(
                (results) => {
                    this.tabAlerts = results;

                    let hasAlerts = false;
                    for (let idx = 0; idx < results.length; idx++) {
                        const alerts: AlertResultDto[] = results[idx].alerts;
                        if (alerts.filter((r) => r.isCurrent).length > 0) {
                            this.selectedViewIndex = idx;
                            hasAlerts = true;
                            break;
                        }
                    }
                    if (this.tabAlerts.length > 0 && this.selectedViewIndex === -1) {
                        this.selectedViewIndex = 0;
                    }

                    this.selectedTabData = this.tabAlerts[this.selectedViewIndex];
                    this.visible = hasAlerts || forceShow;
                    this.loadingVisible = false;
                },
                (error) => {
                    this.loadingVisible = false;
                    this.errorService.displayError(error);
                }
            );
        }
    }

    onShown(e): void {
        e.component.repaint();
        this.alertsList.loadAlerts(this.selectedTabData);
    }

    onHidden(): void {
        this.selectedTabData = null;
        this.selectedViewIndex = -1;
        this.visible = false;
        this.alertsList.reset();
    }

    onTabItemClick(e): void {
        this.selectedTabData = this.tabAlerts[e.itemIndex];
        this.alertsList.loadAlerts(this.selectedTabData);
    }

    onAddClick(): void {
        this.alertsList.addNew();
    }

    onSaveOrUpdate(flag: boolean): void {
        const selectedTab = this.viewModes[this.selectedViewIndex];
        if (selectedTab.id === 'owner-alerts') {
            this.alertServices
                .getOwnerAlerts(this.selectedContext.customerId, this.selectedContext.communityId)
                .subscribe(this.updateSelectedTabData.bind(this));
        }
        if (selectedTab.id === 'community-alerts') {
            this.alertServices
                .getCommunityAlerts(this.selectedContext.communityId)
                .subscribe(this.updateSelectedTabData.bind(this));
        }
    }

    updateSelectedTabData(result: CommunityAlertResultDto | OwnerAlertResultDto): void {
        this.tabAlerts[this.selectedViewIndex] = result;
        this.selectedTabData = result;
        this.alertsList.loadAlerts(this.selectedTabData);
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
        if (this.isSmallScreen) {
            this.popupWidth = '95%';
            this.popupHeight = '90%';
        } else {
            this.popupWidth = '30%';
            this.popupHeight = '70%';
        }
    }
}
